import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthToken } from "@/hooks/useAuthToken";
import Alerta from "../../alert/Alerta";
import "./GestionDonaciones.css";
import { VITE_BACKEND_URL } from "../../../api/config";
import GestionBeneficiarios from "./GestionBeneficiarios";

interface Beneficiario {
  beneficiario_id: number;
  tipo: string;
  nombre: string;
  nombre_publico: string | null;
  descripcion_publica: string | null;
  localidad_publica: string | null;
  autorizacion_publicacion: number | boolean;
  activo: number | boolean;
}

interface Donacion {
  donacion_id: number;
  importe: number | string;
  fecha: string;
  concepto: string;
  profesionales: string;
  metodo: string;
  creado_en: string;
  beneficiario_id?: number | null;
  beneficiario_nombre?: string | null;
  beneficiario_tipo?: string | null;
  concepto_publico?: string | null;
  publicar_en_historico?: number | boolean;
}

type EstadoTrimestre = "Pendiente" | "Presentado" | "Regularizado";

interface TrimestreEconomico {
  anio: number;
  trimestre: number;
  ingresos_brutos: number | string;
  iva_regularizado: number | string | null;
  otros_impuestos_hacienda: number | string | null;
  gastos_empresa: number | string | null;
  estado: EstadoTrimestre;
}

interface AjusteEconomicoAnual {
  anio: number;
  iva_ajuste_anual: number | string | null;
  otros_impuestos_ajuste_anual: number | string | null;
  gastos_ajuste_anual: number | string | null;
}

interface ResumenEconomico {
  anio: number;
  ingresos_brutos: number | string;
  iva_regularizado: number | string | null;
  iva_ajuste_anual: number | string | null;
  iva_total: number | string | null;
  ingresos_sin_iva: number | string | null;
  otros_impuestos_hacienda: number | string | null;
  otros_impuestos_ajuste_anual: number | string | null;
  otros_impuestos_total: number | string | null;
  gastos_empresa: number | string | null;
  gastos_ajuste_anual: number | string | null;
  gastos_total: number | string | null;
  beneficio_neto: number | string | null;
  objetivo_donacion: number | string | null;
  total_donaciones: number | string;
  pendiente_donar: number | string | null;
  datos_fiscales_completos: boolean;
  trimestres: TrimestreEconomico[];
}

interface TrimestreDraft {
  iva_regularizado: string;
  otros_impuestos_hacienda: string;
  gastos_empresa: string;
  estado: EstadoTrimestre;
}

interface AjusteDraft {
  iva_ajuste_anual: string;
  otros_impuestos_ajuste_anual: string;
  gastos_ajuste_anual: string;
}

const API_URL = VITE_BACKEND_URL?.replace(/\/+$/, "");

const euroFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
});

const formatEuro = (value: number | string) =>
  euroFormatter.format(Number(value) || 0);

const formatNullableEuro = (value: number | string | null) =>
  value === null || value === undefined ? "Pendiente" : formatEuro(value);

const inputValue = (value: number | string | null) =>
  value === null || value === undefined ? "" : Number(value).toFixed(2);

const formatDate = (date: string) => {
  const normalized = date.slice(0, 10);
  const [year, month, day] = normalized.split("-");
  return `${day}/${month}/${year}`;
};

const currentYear = new Date().getFullYear();

const emptySummary = (anio: number): ResumenEconomico => ({
  anio,
  ingresos_brutos: 0,
  iva_regularizado: null,
  iva_ajuste_anual: null,
  iva_total: null,
  ingresos_sin_iva: null,
  otros_impuestos_hacienda: null,
  otros_impuestos_ajuste_anual: null,
  otros_impuestos_total: null,
  gastos_empresa: null,
  gastos_ajuste_anual: null,
  gastos_total: null,
  beneficio_neto: null,
  objetivo_donacion: null,
  total_donaciones: 0,
  pendiente_donar: null,
  datos_fiscales_completos: false,
  trimestres: [],
});

const emptyAjusteDraft = (): AjusteDraft => ({
  iva_ajuste_anual: "",
  otros_impuestos_ajuste_anual: "",
  gastos_ajuste_anual: "",
});

const toTrimestreDraft = (trimestre: TrimestreEconomico): TrimestreDraft => ({
  iva_regularizado: inputValue(trimestre.iva_regularizado),
  otros_impuestos_hacienda: inputValue(trimestre.otros_impuestos_hacienda),
  gastos_empresa: inputValue(trimestre.gastos_empresa),
  estado: trimestre.estado,
});

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getErrorMessage = (data: unknown, fallback: string): string => {
  if (isObject(data)) {
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
  }

  return fallback;
};

// Devuelve null cuando el campo está vacío.
// Devuelve undefined cuando el importe no es válido.
const parseNullableAmount = (value: string): number | null | undefined => {
  if (!value.trim()) return null;

  const amount = Number(value);

  if (
    !Number.isFinite(amount) ||
    Math.abs(amount * 100 - Math.round(amount * 100)) > 0.000001
  ) {
    return undefined;
  }

  return Math.round(amount * 100) / 100;
};

const isEnabled = (value: number | boolean | null | undefined): boolean =>
  value === 1 || value === true;

const GestionDonaciones: React.FC = () => {
  const navigate = useNavigate();
  const { roles, token } = useAuthToken();

  const [donaciones, setDonaciones] = useState<Donacion[]>([]);
  const [beneficiarios, setBeneficiarios] = useState<Beneficiario[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadingBeneficiarios, setLoadingBeneficiarios] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [alerta, setAlerta] = useState<{
    mensaje: string;
    type: "success" | "error" | "warning";
  } | null>(null);

  const [form, setForm] = useState({
    importe: "",
    fecha: new Date().toLocaleDateString("en-CA"),
    concepto: "",
    metodo: "Transferencia bancaria",
    beneficiario_id: "",
    concepto_publico: "",
    publicar_en_historico: false,
  });

  const [anio, setAnio] = useState(currentYear);
  const [resumen, setResumen] = useState<ResumenEconomico>(
    emptySummary(currentYear),
  );

  const [trimestres, setTrimestres] = useState<TrimestreEconomico[]>([]);
  const [trimestreDrafts, setTrimestreDrafts] = useState<
    Record<number, TrimestreDraft>
  >({});

  const [ajusteDraft, setAjusteDraft] =
    useState<AjusteDraft>(emptyAjusteDraft());

  const [loadingResumen, setLoadingResumen] = useState(true);
  const [savingTrimestre, setSavingTrimestre] = useState<number | null>(null);
  const [savingAjuste, setSavingAjuste] = useState(false);

  const rolesClean = Array.isArray(roles)
    ? roles
        .filter((role) => typeof role === "string")
        .map((role) => role.toLowerCase().trim().replace(/\s|_/g, ""))
    : [];

  const isSuperAdmin = rolesClean.includes("superadmin");

  const beneficiariosActivos = beneficiarios.filter((beneficiario) =>
    isEnabled(beneficiario.activo),
  );

  const beneficiarioSeleccionado = beneficiarios.find(
    (beneficiario) =>
      String(beneficiario.beneficiario_id) === form.beneficiario_id,
  );

  const puedePublicarBeneficiario =
    !!beneficiarioSeleccionado &&
    isEnabled(beneficiarioSeleccionado.activo) &&
    isEnabled(beneficiarioSeleccionado.autorizacion_publicacion) &&
    !!beneficiarioSeleccionado.nombre_publico?.trim();

  const cargarDonaciones = useCallback(async () => {
    if (!API_URL || !token) {
      throw new Error("No está configurada la conexión con el servidor.");
    }

    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/api/superadmin/donaciones`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        cache: "no-store",
      });

      const data: unknown = await response.json();

      if (!response.ok) {
        throw new Error(
          getErrorMessage(data, "No se han podido consultar las donaciones."),
        );
      }

      if (!Array.isArray(data)) {
        throw new Error("La respuesta de las donaciones no es válida.");
      }

      setDonaciones(data as Donacion[]);
    } catch (error) {
      setAlerta({
        mensaje:
          error instanceof Error
            ? error.message
            : "Error al cargar las donaciones.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const cargarBeneficiarios = useCallback(async () => {
    if (!API_URL || !token) {
      setBeneficiarios([]);
      return;
    }

    try {
      setLoadingBeneficiarios(true);

      const response = await fetch(
        `${API_URL}/api/superadmin/donaciones/beneficiarios`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          cache: "no-store",
        },
      );

      const data: unknown = await response.json();

      if (!response.ok) {
        throw new Error(
          getErrorMessage(
            data,
            "No se han podido consultar los beneficiarios.",
          ),
        );
      }

      if (!Array.isArray(data)) {
        throw new Error("La respuesta de los beneficiarios no es válida.");
      }

      setBeneficiarios(data as Beneficiario[]);
    } catch (error) {
      setAlerta({
        mensaje:
          error instanceof Error
            ? error.message
            : "Error al cargar los beneficiarios.",
        type: "error",
      });
    } finally {
      setLoadingBeneficiarios(false);
    }
  }, [token]);

  const cargarDatosEconomicos = useCallback(
    async (year: number) => {
      if (!API_URL || !token) return;

      try {
        setLoadingResumen(true);

        const headers = {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        };

        const baseUrl = `${API_URL}/api/superadmin/donaciones`;

        const [summaryResponse, quartersResponse, adjustmentResponse] =
          await Promise.all([
            fetch(`${baseUrl}/resumen-anual/${year}`, {
              headers,
              cache: "no-store",
            }),
            fetch(`${baseUrl}/trimestres/${year}`, {
              headers,
              cache: "no-store",
            }),
            fetch(`${baseUrl}/ajuste-anual/${year}`, {
              headers,
              cache: "no-store",
            }),
          ]);

        const [summaryData, quartersData, adjustmentData]: unknown[] =
          await Promise.all([
            summaryResponse.json(),
            quartersResponse.json(),
            adjustmentResponse.json(),
          ]);

        if (!summaryResponse.ok) {
          throw new Error(
            getErrorMessage(
              summaryData,
              "No se ha podido consultar el resumen económico.",
            ),
          );
        }

        if (!quartersResponse.ok) {
          throw new Error(
            getErrorMessage(
              quartersData,
              "No se han podido consultar los trimestres.",
            ),
          );
        }

        if (!adjustmentResponse.ok) {
          throw new Error(
            getErrorMessage(
              adjustmentData,
              "No se han podido consultar los ajustes anuales.",
            ),
          );
        }

        if (
          !isObject(summaryData) ||
          !isObject(quartersData) ||
          !Array.isArray(quartersData.trimestres) ||
          !isObject(adjustmentData)
        ) {
          throw new Error("La respuesta económica del servidor no es válida.");
        }

        const summary = summaryData as unknown as ResumenEconomico;
        const quarterList = quartersData.trimestres as TrimestreEconomico[];
        const adjustment = adjustmentData as unknown as AjusteEconomicoAnual;

        if (
          summary.anio !== year ||
          adjustment.anio !== year ||
          quarterList.length !== 4 ||
          quarterList.some(
            (item, index) => item.anio !== year || item.trimestre !== index + 1,
          )
        ) {
          throw new Error(
            "Los datos del ejercicio no tienen el formato esperado.",
          );
        }

        setResumen(summary);
        setTrimestres(quarterList);

        const drafts: Record<number, TrimestreDraft> = {};

        quarterList.forEach((item) => {
          drafts[item.trimestre] = toTrimestreDraft(item);
        });

        setTrimestreDrafts(drafts);
        setAjusteDraft({
          iva_ajuste_anual: inputValue(adjustment.iva_ajuste_anual),
          otros_impuestos_ajuste_anual: inputValue(
            adjustment.otros_impuestos_ajuste_anual,
          ),
          gastos_ajuste_anual: inputValue(adjustment.gastos_ajuste_anual),
        });
      } catch (error) {
        setAlerta({
          mensaje:
            error instanceof Error
              ? error.message
              : "Error al cargar los datos económicos.",
          type: "error",
        });

        setResumen(emptySummary(year));
        setTrimestres([]);
        setTrimestreDrafts({});
        setAjusteDraft(emptyAjusteDraft());
      } finally {
        setLoadingResumen(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (isSuperAdmin && token) {
      void cargarDonaciones();
    }
  }, [isSuperAdmin, token, cargarDonaciones]);

  useEffect(() => {
    if (isSuperAdmin && token) {
      void cargarBeneficiarios();
    }
  }, [isSuperAdmin, token, cargarBeneficiarios]);

  useEffect(() => {
    if (isSuperAdmin && token) {
      void cargarDatosEconomicos(anio);
    }
  }, [isSuperAdmin, token, anio, cargarDatosEconomicos]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handlePublicationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setForm((previous) => ({
      ...previous,
      publicar_en_historico: event.target.checked,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!API_URL || !token) {
      setAlerta({
        mensaje: "No está configurada la conexión con el servidor.",
        type: "error",
      });
      return;
    }

    const importe = Number(form.importe);

    if (
      !Number.isFinite(importe) ||
      importe <= 0 ||
      Math.abs(importe * 100 - Math.round(importe * 100)) > 0.000001
    ) {
      setAlerta({
        mensaje:
          "Introduce un importe válido superior a cero y con dos decimales como máximo.",
        type: "error",
      });
      return;
    }

    if (
      !form.fecha ||
      !form.concepto.trim() ||
      !form.beneficiario_id ||
      !form.metodo.trim()
    ) {
      setAlerta({
        mensaje: "Completa todos los campos obligatorios.",
        type: "warning",
      });
      return;
    }

    const beneficiario = beneficiarios.find(
      (item) => String(item.beneficiario_id) === form.beneficiario_id,
    );

    if (!beneficiario || !isEnabled(beneficiario.activo)) {
      setAlerta({
        mensaje:
          "Selecciona un beneficiario existente y activo para registrar la donación.",
        type: "warning",
      });
      return;
    }

    if (form.publicar_en_historico) {
      if (
        !isEnabled(beneficiario.autorizacion_publicacion) ||
        !beneficiario.nombre_publico?.trim()
      ) {
        setAlerta({
          mensaje:
            "Este beneficiario no tiene autorización y nombre público configurados. No se puede publicar la donación.",
          type: "warning",
        });
        return;
      }

      if (!form.concepto_publico.trim()) {
        setAlerta({
          mensaje:
            "Introduce el concepto que se mostrará en el histórico público.",
          type: "warning",
        });
        return;
      }
    }

    try {
      setSaving(true);

      const response = await fetch(`${API_URL}/api/superadmin/donaciones`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          importe: Math.round(importe * 100) / 100,
          fecha: form.fecha,
          concepto: form.concepto.trim(),

          // Se conserva el campo antiguo para compatibilidad con el backend.
          // El beneficiario real queda vinculado mediante beneficiario_id.
          profesionales: beneficiario.nombre,

          metodo: form.metodo.trim(),
          beneficiario_id: beneficiario.beneficiario_id,
          concepto_publico: form.concepto_publico.trim(),
          publicar_en_historico: form.publicar_en_historico,
        }),
      });

      const data: unknown = await response.json();

      if (!response.ok) {
        throw new Error(
          getErrorMessage(data, "No se pudo registrar la donación."),
        );
      }

      setForm({
        importe: "",
        fecha: new Date().toLocaleDateString("en-CA"),
        concepto: "",
        metodo: "Transferencia bancaria",
        beneficiario_id: "",
        concepto_publico: "",
        publicar_en_historico: false,
      });

      setAlerta({
        mensaje: "Donación registrada correctamente.",
        type: "success",
      });

      await Promise.all([cargarDonaciones(), cargarDatosEconomicos(anio)]);
    } catch (error) {
      setAlerta({
        mensaje:
          error instanceof Error
            ? error.message
            : "Error al registrar la donación.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("¿Seguro que quieres eliminar esta donación?")) {
      return;
    }

    if (!API_URL || !token) return;

    try {
      setDeletingId(id);

      const response = await fetch(
        `${API_URL}/api/superadmin/donaciones/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      );

      const data: unknown = await response.json();

      if (!response.ok) {
        throw new Error(
          getErrorMessage(data, "No se pudo eliminar la donación."),
        );
      }

      setDonaciones((previous) =>
        previous.filter((item) => item.donacion_id !== id),
      );

      setAlerta({
        mensaje: "Donación eliminada correctamente.",
        type: "success",
      });

      await cargarDatosEconomicos(anio);
    } catch (error) {
      setAlerta({
        mensaje:
          error instanceof Error
            ? error.message
            : "Error al eliminar la donación.",
        type: "error",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleQuarterChange = (
    trimestre: number,
    field: keyof TrimestreDraft,
    value: string,
  ) => {
    setTrimestreDrafts((previous) => ({
      ...previous,
      [trimestre]: {
        ...previous[trimestre],
        [field]: value,
      },
    }));
  };

  const guardarTrimestre = async (
    event: React.FormEvent<HTMLFormElement>,
    trimestre: number,
  ) => {
    event.preventDefault();

    if (!API_URL || !token) return;

    const draft = trimestreDrafts[trimestre];

    if (!draft) return;

    const iva = parseNullableAmount(draft.iva_regularizado);
    const impuestos = parseNullableAmount(draft.otros_impuestos_hacienda);
    const gastos = parseNullableAmount(draft.gastos_empresa);

    if (iva === undefined || impuestos === undefined || gastos === undefined) {
      setAlerta({
        mensaje:
          "Revisa los importes. Utiliza números con un máximo de dos decimales.",
        type: "error",
      });
      return;
    }

    try {
      setSavingTrimestre(trimestre);

      const response = await fetch(
        `${API_URL}/api/superadmin/donaciones/trimestres/${anio}/${trimestre}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            iva_regularizado: iva,
            otros_impuestos_hacienda: impuestos,
            gastos_empresa: gastos,
            estado: draft.estado,
          }),
        },
      );

      const data: unknown = await response.json();

      if (!response.ok) {
        throw new Error(
          getErrorMessage(
            data,
            "No se pudieron guardar los datos del trimestre.",
          ),
        );
      }

      await cargarDatosEconomicos(anio);

      setAlerta({
        mensaje: `Datos del trimestre ${trimestre} guardados correctamente.`,
        type: "success",
      });
    } catch (error) {
      setAlerta({
        mensaje:
          error instanceof Error
            ? error.message
            : "Error al guardar el trimestre.",
        type: "error",
      });
    } finally {
      setSavingTrimestre(null);
    }
  };

  const handleAdjustmentChange = (field: keyof AjusteDraft, value: string) => {
    setAjusteDraft((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const guardarAjusteAnual = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (!API_URL || !token) return;

    const iva = parseNullableAmount(ajusteDraft.iva_ajuste_anual);
    const impuestos = parseNullableAmount(
      ajusteDraft.otros_impuestos_ajuste_anual,
    );
    const gastos = parseNullableAmount(ajusteDraft.gastos_ajuste_anual);

    if (iva === undefined || impuestos === undefined || gastos === undefined) {
      setAlerta({
        mensaje:
          "Revisa los ajustes. Utiliza números con un máximo de dos decimales.",
        type: "error",
      });
      return;
    }

    try {
      setSavingAjuste(true);

      const response = await fetch(
        `${API_URL}/api/superadmin/donaciones/ajuste-anual/${anio}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            iva_ajuste_anual: iva,
            otros_impuestos_ajuste_anual: impuestos,
            gastos_ajuste_anual: gastos,
          }),
        },
      );

      const data: unknown = await response.json();

      if (!response.ok) {
        throw new Error(
          getErrorMessage(data, "No se pudieron guardar los ajustes anuales."),
        );
      }

      await cargarDatosEconomicos(anio);

      setAlerta({
        mensaje: "Ajustes anuales guardados correctamente.",
        type: "success",
      });
    } catch (error) {
      setAlerta({
        mensaje:
          error instanceof Error
            ? error.message
            : "Error al guardar los ajustes anuales.",
        type: "error",
      });
    } finally {
      setSavingAjuste(false);
    }
  };

  const totalDonaciones = donaciones.reduce(
    (total, item) => total + Number(item.importe),
    0,
  );

  if (!isSuperAdmin) {
    return (
      <div className="donaciones-denied">
        <h2>Acceso restringido</h2>
        <p>Necesitas permisos de SuperAdmin para gestionar donaciones.</p>
        <button onClick={() => navigate("/backoffice")}>
          Volver al Backoffice
        </button>
      </div>
    );
  }

  return (
    <main className="donaciones-page">
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}

      <header className="donaciones-header">
        <div>
          <span className="donaciones-eyebrow">LINKSERVS · SUPERADMIN</span>
          <h1>Gestión de donaciones</h1>
          <p>Control económico y registro de las aportaciones sociales.</p>
        </div>

        <button
          className="donaciones-back"
          onClick={() => navigate("/backoffice")}
        >
          Volver al Backoffice
        </button>
      </header>

      <section className="donaciones-summary">
        <div>
          <span>Total de donaciones registradas</span>
          <strong>{donaciones.length}</strong>
        </div>

        <div>
          <span>Importe total donado</span>
          <strong>{euroFormatter.format(totalDonaciones)}</strong>
        </div>
      </section>

      <section className="donaciones-card donaciones-economic">
        <div className="donaciones-card-heading donaciones-economic-heading">
          <div>
            <h2>Gestión económica del ejercicio</h2>
            <p>
              Registra los datos fiscales de cada trimestre y los ajustes
              facilitados por la gestoría al cierre del ejercicio.
            </p>
          </div>

          <label className="donaciones-year-selector">
            Año del ejercicio
            <select
              value={anio}
              onChange={(event) => setAnio(Number(event.target.value))}
            >
              {Array.from(
                { length: Math.max(5, currentYear - 2020 + 2) },
                (_, index) => currentYear + 1 - index,
              ).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loadingResumen ? (
          <p className="donaciones-empty">Cargando datos económicos...</p>
        ) : (
          <>
            <div className="donaciones-section-title">
              <div>
                <h3>Datos económicos trimestrales</h3>
                <p>
                  Los ingresos brutos se obtienen automáticamente de los pagos
                  completados. Introduce los importes fiscales cuando estén
                  disponibles.
                </p>
              </div>
            </div>

            <div className="donaciones-quarter-grid">
              {trimestres.map((trimestre) => {
                const draft = trimestreDrafts[trimestre.trimestre];

                if (!draft) return null;

                return (
                  <form
                    key={`${anio}-${trimestre.trimestre}`}
                    className="donaciones-quarter-card"
                    onSubmit={(event) =>
                      void guardarTrimestre(event, trimestre.trimestre)
                    }
                  >
                    <div className="donaciones-quarter-heading">
                      <div>
                        <span className="donaciones-quarter-kicker">
                          EJERCICIO {anio}
                        </span>
                        <h3>Trimestre {trimestre.trimestre}</h3>
                      </div>

                      <span
                        className={`donaciones-status donaciones-status--${draft.estado.toLowerCase()}`}
                      >
                        {draft.estado}
                      </span>
                    </div>

                    <div className="donaciones-quarter-income">
                      <span>Ingresos brutos del trimestre</span>
                      <strong>{formatEuro(trimestre.ingresos_brutos)}</strong>
                      <small>Calculados automáticamente · IVA incluido</small>
                    </div>

                    <label>
                      IVA regularizado (€)
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Pendiente de informar"
                        value={draft.iva_regularizado}
                        onChange={(event) =>
                          handleQuarterChange(
                            trimestre.trimestre,
                            "iva_regularizado",
                            event.target.value,
                          )
                        }
                      />
                    </label>

                    <label>
                      Otros impuestos de Hacienda (€)
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Pendiente de informar"
                        value={draft.otros_impuestos_hacienda}
                        onChange={(event) =>
                          handleQuarterChange(
                            trimestre.trimestre,
                            "otros_impuestos_hacienda",
                            event.target.value,
                          )
                        }
                      />
                    </label>

                    <label>
                      Gastos de empresa (€)
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Pendiente de informar"
                        value={draft.gastos_empresa}
                        onChange={(event) =>
                          handleQuarterChange(
                            trimestre.trimestre,
                            "gastos_empresa",
                            event.target.value,
                          )
                        }
                      />
                    </label>

                    <label>
                      Estado del trimestre
                      <select
                        value={draft.estado}
                        onChange={(event) =>
                          handleQuarterChange(
                            trimestre.trimestre,
                            "estado",
                            event.target.value,
                          )
                        }
                      >
                        <option value="Pendiente">Pendiente</option>
                        <option value="Presentado">Presentado</option>
                        <option value="Regularizado">Regularizado</option>
                      </select>
                    </label>

                    <button
                      type="submit"
                      className="donaciones-submit donaciones-quarter-submit"
                      disabled={savingTrimestre !== null}
                    >
                      {savingTrimestre === trimestre.trimestre
                        ? "Guardando..."
                        : "Guardar trimestre"}
                    </button>
                  </form>
                );
              })}
            </div>

            <section className="donaciones-adjustment">
              <div className="donaciones-section-title">
                <div>
                  <h3>Ajustes económicos anuales</h3>
                  <p>
                    Introduce aquí las regularizaciones adicionales del
                    ejercicio. Se sumarán a los importes trimestrales. También
                    se permiten importes negativos.
                  </p>
                </div>
              </div>

              <form
                className="donaciones-adjustment-form"
                onSubmit={(event) => void guardarAjusteAnual(event)}
              >
                <label>
                  Ajuste anual de IVA (€)
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Sin ajuste"
                    value={ajusteDraft.iva_ajuste_anual}
                    onChange={(event) =>
                      handleAdjustmentChange(
                        "iva_ajuste_anual",
                        event.target.value,
                      )
                    }
                  />
                </label>

                <label>
                  Ajuste anual de otros impuestos (€)
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Sin ajuste"
                    value={ajusteDraft.otros_impuestos_ajuste_anual}
                    onChange={(event) =>
                      handleAdjustmentChange(
                        "otros_impuestos_ajuste_anual",
                        event.target.value,
                      )
                    }
                  />
                </label>

                <label>
                  Ajuste anual de gastos (€)
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Sin ajuste"
                    value={ajusteDraft.gastos_ajuste_anual}
                    onChange={(event) =>
                      handleAdjustmentChange(
                        "gastos_ajuste_anual",
                        event.target.value,
                      )
                    }
                  />
                </label>

                <button
                  type="submit"
                  className="donaciones-submit"
                  disabled={savingAjuste}
                >
                  {savingAjuste ? "Guardando..." : "Guardar ajustes anuales"}
                </button>
              </form>
            </section>

            <section className="donaciones-results">
              <div className="donaciones-section-title">
                <div>
                  <h3>Resumen económico de {anio}</h3>
                  <p>
                    Resultado calculado automáticamente a partir de los pagos,
                    los cuatro trimestres y los ajustes anuales.
                  </p>
                </div>

                <span
                  className={`donaciones-fiscal-status ${
                    resumen.datos_fiscales_completos
                      ? "donaciones-fiscal-status--complete"
                      : "donaciones-fiscal-status--pending"
                  }`}
                >
                  {resumen.datos_fiscales_completos
                    ? "Datos fiscales completos"
                    : "Regularización pendiente"}
                </span>
              </div>

              <div className="donaciones-economic-grid">
                <div className="donaciones-economic-item">
                  <span>Ingresos brutos de suscripciones</span>
                  <strong>{formatEuro(resumen.ingresos_brutos)}</strong>
                  <small>Importes cobrados, IVA incluido</small>
                </div>

                <div className="donaciones-economic-item">
                  <span>IVA total · trimestres y ajuste</span>
                  <strong>
                    {resumen.datos_fiscales_completos
                      ? formatNullableEuro(resumen.iva_total)
                      : "Pendiente"}
                  </strong>
                  <small>
                    Trimestral: {formatNullableEuro(resumen.iva_regularizado)}
                    {" · "}
                    Ajuste: {formatNullableEuro(resumen.iva_ajuste_anual)}
                  </small>
                </div>

                <div className="donaciones-economic-item">
                  <span>Ingresos sin IVA</span>
                  <strong>
                    {formatNullableEuro(resumen.ingresos_sin_iva)}
                  </strong>
                  <small>Ingresos brutos menos IVA total</small>
                </div>

                <div className="donaciones-economic-item">
                  <span>Otros impuestos · total</span>
                  <strong>
                    {resumen.datos_fiscales_completos
                      ? formatNullableEuro(resumen.otros_impuestos_total)
                      : "Pendiente"}
                  </strong>
                  <small>
                    Trimestral:{" "}
                    {formatNullableEuro(resumen.otros_impuestos_hacienda)}
                    {" · "}
                    Ajuste:{" "}
                    {formatNullableEuro(resumen.otros_impuestos_ajuste_anual)}
                  </small>
                </div>

                <div className="donaciones-economic-item">
                  <span>Gastos de empresa · total</span>
                  <strong>
                    {resumen.datos_fiscales_completos
                      ? formatNullableEuro(resumen.gastos_total)
                      : "Pendiente"}
                  </strong>
                  <small>
                    Trimestral: {formatNullableEuro(resumen.gastos_empresa)}
                    {" · "}
                    Ajuste: {formatNullableEuro(resumen.gastos_ajuste_anual)}
                  </small>
                </div>

                <div className="donaciones-economic-item">
                  <span>Beneficio neto</span>
                  <strong>{formatNullableEuro(resumen.beneficio_neto)}</strong>
                  <small>Después de impuestos y gastos</small>
                </div>

                <div className="donaciones-economic-item">
                  <span>Objetivo de donación · 50 %</span>
                  <strong>
                    {formatNullableEuro(resumen.objetivo_donacion)}
                  </strong>
                  <small>Mitad del beneficio neto positivo</small>
                </div>

                <div className="donaciones-economic-item">
                  <span>Donaciones realizadas en {anio}</span>
                  <strong>{formatEuro(resumen.total_donaciones)}</strong>
                  <small>Registradas en este ejercicio</small>
                </div>

                <div className="donaciones-economic-item donaciones-economic-item--highlight">
                  <span>Pendiente de donar</span>
                  <strong>{formatNullableEuro(resumen.pendiente_donar)}</strong>
                  <small>Objetivo menos donaciones realizadas</small>
                </div>
              </div>

              <p className="donaciones-economic-note">
                Las donaciones no se consideran ingresos de la empresa. Se
                muestran por separado y se descuentan únicamente del objetivo
                pendiente de donar. Los campos vacíos indican datos todavía no
                informados; introduce 0,00 cuando el importe real sea cero.
              </p>
            </section>
          </>
        )}
      </section>

      <GestionBeneficiarios onBeneficiariosChanged={cargarBeneficiarios} />

      <section className="donaciones-card">
        <div className="donaciones-card-heading">
          <h2>Registrar una donación</h2>
          <p>
            Cada donación se asigna a un único beneficiario. Puedes registrar
            varias donaciones independientes para la misma persona o entidad.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="donaciones-form">
          <div className="donaciones-form-grid">
            <label>
              Importe (€)
              <input
                type="number"
                name="importe"
                min="0.01"
                step="0.01"
                placeholder="Ej. 250.00"
                value={form.importe}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Fecha de la donación
              <input
                type="date"
                name="fecha"
                value={form.fecha}
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <label>
            Concepto interno de la donación
            <input
              type="text"
              name="concepto"
              maxLength={500}
              placeholder="Ej. Ayuda económica para el programa social"
              value={form.concepto}
              onChange={handleChange}
              required
            />
            <small>
              Este concepto se guarda en la gestión interna y no se publica
              automáticamente.
            </small>
          </label>

          <div className="donaciones-form-grid">
            <label>
              Método utilizado
              <select
                name="metodo"
                value={form.metodo}
                onChange={handleChange}
                required
              >
                <option value="Transferencia bancaria">
                  Transferencia bancaria
                </option>
                <option value="Efectivo">Efectivo</option>
                <option value="Cheque">Cheque</option>
                <option value="Otro">Otro</option>
              </select>
            </label>
          </div>

          <label>
            Beneficiario de esta donación
            <select
              name="beneficiario_id"
              value={form.beneficiario_id}
              onChange={handleChange}
              required
              disabled={
                loadingBeneficiarios || beneficiariosActivos.length === 0
              }
            >
              <option value="">
                {loadingBeneficiarios
                  ? "Cargando beneficiarios..."
                  : beneficiariosActivos.length === 0
                    ? "No hay beneficiarios activos"
                    : "Selecciona un beneficiario"}
              </option>

              {beneficiariosActivos.map((beneficiario) => (
                <option
                  key={beneficiario.beneficiario_id}
                  value={beneficiario.beneficiario_id}
                >
                  {beneficiario.nombre} · {beneficiario.tipo}
                </option>
              ))}
            </select>
            <small>
              Selecciona una ficha existente. Los datos privados del
              beneficiario no se mostrarán en el histórico público.
            </small>
          </label>

          {beneficiarioSeleccionado && (
            <div className="donaciones-beneficiary-preview">
              <strong>Beneficiario seleccionado</strong>
              <span>{beneficiarioSeleccionado.nombre}</span>
              <small>Tipo: {beneficiarioSeleccionado.tipo}</small>

              {isEnabled(beneficiarioSeleccionado.autorizacion_publicacion) &&
              beneficiarioSeleccionado.nombre_publico?.trim() ? (
                <small>
                  Nombre público autorizado:{" "}
                  {beneficiarioSeleccionado.nombre_publico}
                </small>
              ) : (
                <small>
                  Este beneficiario no está habilitado para aparecer
                  públicamente.
                </small>
              )}
            </div>
          )}

          <label>
            Concepto para el histórico público
            <textarea
              name="concepto_publico"
              rows={3}
              maxLength={500}
              placeholder="Describe brevemente la ayuda que se mostrará públicamente."
              value={form.concepto_publico}
              onChange={handleChange}
              disabled={!puedePublicarBeneficiario}
            />
            <small>
              Este texto solo se mostrará si marcas la publicación y el
              beneficiario tiene autorización registrada.
            </small>
          </label>

          <label className="donaciones-publication-option">
            <input
              type="checkbox"
              name="publicar_en_historico"
              checked={form.publicar_en_historico}
              onChange={handlePublicationChange}
              disabled={!puedePublicarBeneficiario}
            />
            <span>
              Incluir esta donación en el histórico público de ayudas.
            </span>
          </label>

          {!puedePublicarBeneficiario && beneficiarioSeleccionado && (
            <p className="donaciones-publication-warning">
              Para publicar esta ayuda, la ficha del beneficiario debe tener
              autorización de publicación y un nombre público configurado.
            </p>
          )}

          <button
            type="submit"
            className="donaciones-submit"
            disabled={saving || loadingBeneficiarios}
          >
            {saving ? "Guardando..." : "Registrar donación"}
          </button>
        </form>
      </section>

      <section className="donaciones-card donaciones-history">
        <div className="donaciones-card-heading">
          <h2>Donaciones registradas</h2>
          <p>Consulta y administra las aportaciones realizadas.</p>
        </div>

        {loading ? (
          <p className="donaciones-empty">Cargando donaciones...</p>
        ) : donaciones.length === 0 ? (
          <p className="donaciones-empty">
            Todavía no hay donaciones registradas.
          </p>
        ) : (
          <div className="donaciones-table-wrapper">
            <table className="donaciones-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Importe</th>
                  <th>Concepto</th>
                  <th>Método</th>
                  <th>Beneficiario</th>
                  <th>Histórico público</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {donaciones.map((donacion) => {
                  const beneficiarioNombre =
                    donacion.beneficiario_nombre?.trim() ||
                    donacion.profesionales ||
                    "Sin beneficiario asignado";

                  return (
                    <tr key={donacion.donacion_id}>
                      <td>{formatDate(donacion.fecha)}</td>

                      <td className="donaciones-amount">
                        {formatEuro(donacion.importe)}
                      </td>

                      <td>{donacion.concepto}</td>

                      <td>{donacion.metodo || "No especificado"}</td>

                      <td>
                        <div className="donaciones-beneficiary-cell">
                          <strong>{beneficiarioNombre} </strong>
                          {donacion.beneficiario_tipo && (
                            <small>{donacion.beneficiario_tipo}</small>
                          )}
                        </div>
                      </td>

                      <td>
                        {isEnabled(donacion.publicar_en_historico) ? (
                          <span className="donaciones-publication-status donaciones-publication-status--published">
                            Publicada
                          </span>
                        ) : (
                          <span className="donaciones-publication-status">
                            No publicada
                          </span>
                        )}
                      </td>

                      <td>
                        <button
                          className="donaciones-delete"
                          onClick={() =>
                            void handleDelete(donacion.donacion_id)
                          }
                          disabled={deletingId === donacion.donacion_id}
                        >
                          {deletingId === donacion.donacion_id
                            ? "Eliminando..."
                            : "Eliminar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default GestionDonaciones;
